import { Button } from '@bambi/component-library/src/components/Button/Button';
import { CheckBox } from '@bambi/component-library/src/components/inputs/CheckBox/CheckBox';
import { SimpleTooltip } from '@bambi/component-library/src/components/Tooltip/SimpleTooltip';
import {
  ArrowUturnLeftIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import { TripRead } from 'src/common/external/bambi-api/bambiApi';
import {
  renderShortAddress,
  safeRenderShortAddress,
} from 'src/common/formatAddress';
import { TripCardInternal } from 'src/features/dispatch/trips/TripCard/TripCard';

import { RouteTripTemplateFormValues } from '../types';

interface RouteTripTemplateCardProps {
  tripTemplate: RouteTripTemplateFormValues & { __id: string };
  variant?: 'route-template' | 'pickup' | 'dropoff' | 'multi-load';
  canSelect?: boolean;
  isSelected?: boolean;
  handleEditTripMatcher?: (tripTemplate: RouteTripTemplateFormValues) => void;
  handleDeleteTripMatcher?: (tripTemplate: RouteTripTemplateFormValues) => void;
  onSelect?: (tripTemplate: RouteTripTemplateFormValues) => void;
}

export function RouteTripTemplateCard({
  tripTemplate,
  variant = 'route-template',
  canSelect,
  isSelected,
  handleEditTripMatcher,
  handleDeleteTripMatcher,
  onSelect,
}: RouteTripTemplateCardProps) {
  let pickupAddress = '';
  let dropoffAddress = '';

  try {
    // The location field populates this field, while the
    // retrieve returns as 'address', so try here first and then
    // fallback to the address field
    pickupAddress = renderShortAddress(
      tripTemplate.pickup.address_details ?? ''
    );
    dropoffAddress = renderShortAddress(
      tripTemplate.dropoff.address_details ?? ''
    );
  } catch {
    pickupAddress = safeRenderShortAddress(tripTemplate.pickup.address);
    dropoffAddress = safeRenderShortAddress(tripTemplate.dropoff.address);
  }

  const isEditable = handleEditTripMatcher && handleDeleteTripMatcher;

  return (
    <div className="flex flex-row items-center gap-2">
      {canSelect ? (
        <CheckBox
          checked={isSelected}
          onCheckedChange={() => onSelect?.(tripTemplate)}
        />
      ) : null}

      <TripCardInternal
        key={tripTemplate.__id}
        variant={variant}
        trip={
          {
            id: tripTemplate.id ?? tripTemplate.__id,
            scheduled_pickup_at: tripTemplate.scheduled_pickup_time,
            passenger: tripTemplate.passenger,
            pickup_address: pickupAddress,
            dropoff_address: dropoffAddress,
            space_type: tripTemplate.space_type,
            // Not quite a TripRead, but close enough to render the trip card
          } as unknown as TripRead
        }
        additionalPassengers={tripTemplate.num_accompanying_passengers}
        adornment={
          !isEditable ? (
            <div />
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex flex-row">
                <Button
                  dataTestId={`edit-trip-matcher-${tripTemplate.__id}`}
                  variant="icon"
                  onClick={() => handleEditTripMatcher(tripTemplate)}
                  ariaLabel={`Edit trip matcher`}
                >
                  <div
                    className="flex flex-col items-center text-xs"
                    style={{ fontSize: '0.625rem' }}
                  >
                    <PencilIcon className="w-[24px]" />
                    Edit
                  </div>
                </Button>
                <SimpleTooltip
                  trigger={
                    <div className="flex">
                      <Button
                        dataTestId={`remove-trip-matcher-${tripTemplate.__id}`}
                        variant="icon"
                        onClick={() => handleDeleteTripMatcher(tripTemplate)}
                        ariaLabel={
                          variant === 'multi-load'
                            ? `Restore original trip matcher configuration`
                            : `Delete trip matcher`
                        }
                      >
                        <div
                          className="flex flex-col items-center text-xs"
                          style={{ fontSize: '0.625rem' }}
                        >
                          {variant === 'multi-load' ? (
                            <>
                              <ArrowUturnLeftIcon className="w-[24px] stroke-red-700" />
                              Restore
                            </>
                          ) : (
                            <>
                              <TrashIcon className="w-[24px] stroke-red-700" />
                              Remove
                            </>
                          )}
                        </div>
                      </Button>
                    </div>
                  }
                  content={
                    variant === 'multi-load'
                      ? 'Restore original trip matcher configuration'
                      : 'Delete trip matcher'
                  }
                />
              </div>
            </div>
          )
        }
        hideMenu
      />
    </div>
  );
}
